import { LinkButton, Plus, PrimaryButton, Typography } from '@weareopenr/component-library';
import { useMemo } from 'react';
import classnames from 'classnames/bind';
import SelectableList, { SelectableListOption } from '../SelectableList/SelectableList';
import SelectableListWithInfiniteScroll from '../SelectableListWithInfiniteScroll/SelectableListWithInfiniteScroll';
import { SelectedItemsCollapsible } from '../SelectedItemsCollapsible/SelectedItemsCollapsible';
import { SelectableListSection } from '../SelectableListSection';
import styles from './SelectableListWithControls.module.scss';

const cx = classnames.bind(styles);

export interface SelectableListWithControlsProps {
  options: SelectableListOption[];
  selectedOptionsIds: string[];
  onOptionChange: (selectedOptions: string[]) => void;
  children?: React.ReactNode;
  className?: string;
  scrollContainerClassName?: string;
  collapsible?: {
    selectedOptionsData: SelectableListOption[];
    isDisabled?: boolean;
    isLoading?: boolean;
    isPinned?: boolean;
  };
  listAction?: {
    label: string;
    onClick: () => void;
  };
  listDetails?: {
    title?: string;
    rightText?: string;
    emptyMessage?: string;
    isLoading?: boolean;
  };
  pagination?: {
    hasNextPage: boolean;
    onLoadMore: () => void;
    isLoadingMore?: boolean;
  };
}

const SelectableListWithControls = ({
  options,
  selectedOptionsIds,
  onOptionChange,
  children,
  className,
  scrollContainerClassName,
  collapsible,
  listAction,
  listDetails,
  pagination,
}: SelectableListWithControlsProps) => {
  const isPaginated = Boolean(pagination);

  const allOptionIds = useMemo(
    () => options.filter((option) => !option.disabled).map((option) => option.id),
    [options],
  );

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (allSelected) {
      const filteredOptions = selectedOptionsIds.filter((item) => !allOptionIds.includes(item));
      onOptionChange(filteredOptions);
    } else {
      onOptionChange(Array.from(new Set(selectedOptionsIds.concat(allOptionIds))));
    }
  };

  const selectedSet = new Set(selectedOptionsIds);
  const allSelected = allOptionIds.every((id) => selectedSet.has(id));

  const collapsibleSection =
    collapsible &&
    (listAction && collapsible.selectedOptionsData.length === 0 ? (
      <div className={styles['list-action-container']}>
        <PrimaryButton size="small" type="button" onClick={listAction.onClick} icon={Plus}>
          {listAction.label}
        </PrimaryButton>
      </div>
    ) : (
      <SelectedItemsCollapsible
        count={collapsible.selectedOptionsData.length}
        options={collapsible.selectedOptionsData}
        selectedOptionsIds={selectedOptionsIds}
        onChange={onOptionChange}
        isDisabled={collapsible.isDisabled}
        isLoading={collapsible.isLoading}
      />
    ));

  return (
    <div className={cx('selectable-list-with-controls', className)}>
      <div className={styles['controls-container']}>
        {children && <div className={cx('filters-container')}>{children}</div>}
        {!collapsible && (
          <Typography className={styles['current-selected']} size="xsmall">
            {`Currently selected - ${selectedOptionsIds.length}`}
          </Typography>
        )}
      </div>

      {collapsible?.isPinned && collapsibleSection}

      <div className={cx('list-scroll-container', scrollContainerClassName)}>
        {!collapsible?.isPinned && collapsibleSection}

        <SelectableListSection
          title={listDetails?.title}
          rightText={listDetails?.rightText}
          emptyMessage={listDetails?.emptyMessage}
          isLoading={Boolean(listDetails?.isLoading)}
          isEmpty={options.length === 0}
        >
          {isPaginated ? (
            <SelectableListWithInfiniteScroll
              options={options}
              selectedOptions={selectedOptionsIds}
              onChange={onOptionChange}
              hasNextPage={pagination?.hasNextPage}
              onLoadMore={pagination?.onLoadMore}
              isLoadingMore={pagination?.isLoadingMore}
            />
          ) : (
            <SelectableList options={options} selectedOptions={selectedOptionsIds} onChange={onOptionChange} />
          )}
        </SelectableListSection>
      </div>
      {!isPaginated && (
        <div className={styles['select-all-controls']}>
          {allOptionIds.length > 0 && (
            <LinkButton aria-label="Select all checkbox" className={styles['select-button']} onClick={handleSelectAll}>
              {allSelected ? `Deselect all (${allOptionIds.length})` : `Select all (${allOptionIds.length})`}
            </LinkButton>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectableListWithControls;
